import { useTranslation } from "react-i18next";
import { MessagesSquare, MapPin, Search, CalendarCheck, HandHeart } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTAButtons } from "@/components/CTAButtons";
import { IndependenceStrip } from "@/components/IndependenceStrip";
import { EmergencyNotice } from "@/components/EmergencyNotice";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const STEP_ICONS = [MessagesSquare, MapPin, Search, CalendarCheck, HandHeart];

const HowItWorks = () => {
  const { t } = useTranslation();
  const faqs = t("howItWorksPage.faqs", { returnObjects: true }) || [];
  const steps = ["s1", "s2", "s3", "s4", "s5"];

  return (
    <>
      <Seo
        title={t("howItWorksPage.seoTitle")}
        description={t("howItWorksPage.metaDescription")}
        path="/how-it-works"
        exact
      />
      <PageHero
        eyebrow={t("howItWorksPage.eyebrow")}
        title={t("howItWorksPage.h1")}
        subtitle={t("howItWorksPage.subtitle")}
        crumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.howItWorks") }]}
      >
        <CTAButtons />
      </PageHero>

      <section className="vc-container py-14 sm:py-16">
        <div className="space-y-5">
          {steps.map((k, i) => {
            const Icon = STEP_ICONS[i];
            const n = String(i + 1).padStart(2, "0");
            return (
              <Reveal key={k} delay={i * 0.05}>
                <div className="flex flex-col gap-5 rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-soft sm:flex-row sm:items-center sm:p-8">
                  <span className="font-display shrink-0 text-5xl font-semibold text-brand sm:text-6xl">{n}</span>
                  <span className="hidden h-16 w-px bg-[hsl(var(--vc-line))] sm:block" />
                  <div className="flex items-start gap-4">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-mint text-brand"><Icon className="h-6 w-6" /></span>
                    <div>
                      <h2 className="text-xl font-extrabold text-[hsl(var(--vc-ink))]">{t(`steps.${k}t`)}</h2>
                      <p className="mt-1.5 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{t(`steps.${k}x`)}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <Reveal><IndependenceStrip /></Reveal>
          <Reveal delay={0.05}><EmergencyNotice className="h-full items-center" /></Reveal>
        </div>
      </section>

      <section className="bg-mint py-14 sm:py-16">
        <div className="vc-container max-w-3xl">
          <h2 className="text-3xl font-extrabold tracking-tight text-[hsl(var(--vc-ink))]">{t("howItWorksPage.faqTitle")}</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[hsl(var(--vc-line))]">
                <AccordionTrigger className="text-left text-base font-bold text-[hsl(var(--vc-ink))] hover:no-underline" data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
};

export default HowItWorks;
